import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FiAlertTriangle, FiCheckCircle, FiDownload, FiLoader } from "react-icons/fi";
import { apiV1 } from "api-v2";
import { DateInputWithLabel } from "newui/components/ui/InputWithLabel/DateInputWithLabel";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";

import { DocumentForm } from "./DocumentForm";
import { processFormData, showToast } from "utils/helpers";
import { useCurrentCompany, useCurrentCompanyId } from "hooks/auth";
import { useInsuranceVerification } from "newui/features/Insurance/hooks/useInsuranceVerification";
import {
  getDocument,
  type PostInsuranceDocumentResponse,
} from "newui/features/Insurance/api/insuranceVerificationApi";
import { insuranceVerificationKeys } from "newui/features/Insurance/api/insuranceVerificationKeys";
import { companyDocumentKeys } from "newui/features/Company/CompanyDocuments/companyDocumentKeys";
import { ConfirmExtractedFields } from "newui/features/Insurance/components/ConfirmExtractedFields";
import { MonthlyCapBanner } from "newui/features/Insurance/components/MonthlyCapBanner";
import type { InsuranceOcrJobState } from "newui/features/Insurance/types/InsuranceOcrJob.type";

const INSURANCE_CATEGORY_ID = 4;
const ACTIVE_STATES: InsuranceOcrJobState[] = ["queued", "running"];
const COMPLETED_STATES: InsuranceOcrJobState[] = ["succeeded", "soft_failed", "failed"];

interface AddDocumentProps {
  /** Override initial category_id */
  initialCategoryId?: number | string;
  /** Lock category dropdown to a specific value */
  lockCategory?: boolean;
  /** Pre-selected subcategory ID when locked */
  lockedSubcategoryId?: number | null;
  /** Lock subcategory dropdown */
  lockSubcategory?: boolean;
  onSuccessfulSubmit: () => void;
  /** Called with the new document ID after a successful save */
  onCreated?: (id: number) => void;
}

type Phase = "form" | "verifying" | "review" | "cap_reached";

export const AddDocument: React.FC<AddDocumentProps> = ({
  onSuccessfulSubmit,
  onCreated,
  lockCategory,
  lockSubcategory,
  lockedSubcategoryId,
  initialCategoryId,
}) => {
  const initialValues = {
    name: "",
    category_id: initialCategoryId ?? "",
    sub_category_id: "",
    documentable_type: "App\\Models\\Company",
    file: "",
    extra_info: {
      expiry_date: "",
      accreditation: "",
      relevant_year: "",
      value: "",
      audited: "",
    },
  };
  const currentCompany = useCurrentCompany();
  const companyId = useCurrentCompanyId() as number;
  const profileTypeId = currentCompany?.profile_type?.id;
  const hasInsuranceVerification = useInsuranceVerification();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>("form");
  const [savedDocumentId, setSavedDocumentId] = useState<number | null>(null);
  const [savedDocumentName, setSavedDocumentName] = useState<string>("");
  const [savedStoragePath, setSavedStoragePath] = useState<string | null>(null);
  const [capResetsAt, setCapResetsAt] = useState<string | null>(null);

  // Poll document API while verifying
  const { data: liveDocument } = useQuery({
    queryKey: insuranceVerificationKeys.document(companyId, savedDocumentId ?? 0),
    queryFn: () => getDocument(companyId, savedDocumentId!),
    select: (res) => res.data.data,
    enabled: phase === "verifying" && savedDocumentId !== null,
    refetchInterval: (query) => {
      const doc = query.state.data as { latest_ocr_job?: { state?: string } } | undefined;
      const state = (doc?.latest_ocr_job?.state) as InsuranceOcrJobState | undefined;
      if (state && !ACTIVE_STATES.includes(state)) {
        return false;
      }
      return 5000;
    },
    refetchIntervalInBackground: false,
  });

  const liveOcrState = (liveDocument?.latest_ocr_job?.state ?? liveDocument?.ocr_job?.state ?? null) as InsuranceOcrJobState | null;
  const isOcrComplete = liveOcrState !== null && COMPLETED_STATES.includes(liveOcrState);

  // Transition to review when OCR completes
  useEffect(() => {
    if (phase === "verifying" && isOcrComplete) {
      setPhase("review");
    }
  }, [phase, isOcrComplete]);

  // Mutation for saving the document (works for both insurance and non-insurance)
  const saveMutation = useMutation({
    mutationFn: (formData: FormData) =>
      apiV1.post<PostInsuranceDocumentResponse>(
        `companies/${companyId}/documents`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      ),
    onSuccess: (response) => {
      const { data } = response;
      // Insurance response: { document: {...}, ocr_job: {...} }
      // Non-insurance response: { data: {...} } (DocumentResource)
      // Cap-reached response: { document: {...}, ocr_job: null, cap_reached: true, resets_at: "..." }
      const doc = data.document ?? (data as any).data ?? data;
      const docId = doc?.id;
      const docName = doc?.name ?? "Document";
      const storagePath = doc?.storage_path ?? null;

      // Cap reached — document created without verification, show manual fields
      if (data.cap_reached) {
        setSavedDocumentId(docId);
        setSavedDocumentName(docName);
        setCapResetsAt(data.resets_at ?? null);
        setPhase("cap_reached");
        return;
      }

      const isInsurance = docId && data.ocr_job;

      if (isInsurance) {
        setSavedDocumentId(docId);
        setSavedDocumentName(docName);
        setSavedStoragePath(storagePath);
        setPhase("verifying");
        showToast("Document saved. Reading insurance details...", "Success", true);
      } else {
        queryClient.invalidateQueries({
          queryKey: companyDocumentKeys.documents(companyId),
        });
        if (onCreated && docId) {
          onCreated(docId);
        }
        onSuccessfulSubmit();
        showToast("Document added successfully.", "Success", true);
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message ?? "Failed to save document.";
      showToast(message, "Error");
    },
  });

  const handleSubmit = (values: typeof initialValues) => {
    const formData = processFormData(values);
    saveMutation.mutate(formData);
  };

  const handleConfirmSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: companyDocumentKeys.documents(companyId),
    });
    if (onCreated && savedDocumentId) {
      onCreated(savedDocumentId);
    }
    onSuccessfulSubmit();
  };

  // Phase: verifying (polling for OCR result)
  if (phase === "verifying") {
    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <FiCheckCircle size={18} className="text-green-600" />
          <p className="text-sm font-medium text-gray-700">{savedDocumentName} saved</p>
        </div>

        {savedStoragePath && (
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
            <FiDownload size={16} className="shrink-0 text-blue-600" />
            <a
              href={savedStoragePath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              View Document
            </a>
          </div>
        )}

        <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-4">
          <FiLoader size={20} className="animate-spin text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              Reading insurance details...
            </p>
            <p className="mt-0.5 text-xs text-blue-600">
              This usually takes 10–30 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Phase: review extracted fields
  if (phase === "review" && savedDocumentId && liveDocument) {
    const extraInfo = (liveDocument.extra_info ?? {}) as Record<string, unknown>;
    const ocrJobId = liveDocument.latest_ocr_job?.id ?? liveDocument.ocr_job?.id ?? null;

    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <FiCheckCircle size={18} className="text-green-600" />
          <p className="text-sm font-medium text-gray-700">{savedDocumentName}</p>
        </div>

        {savedStoragePath && (
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
            <FiDownload size={16} className="shrink-0 text-blue-600" />
            <span className="text-sm text-gray-700">Review the original document alongside the extracted data:</span>
            <a
              href={savedStoragePath}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50"
            >
              <FiDownload size={14} />
              View Document
            </a>
          </div>
        )}

        <ConfirmExtractedFields
          documentId={savedDocumentId}
          extraInfo={extraInfo}
          ocrJobState={liveOcrState!}
          ocrJobId={ocrJobId}
          onSuccess={handleConfirmSuccess}
        />
      </div>
    );
  }

  // Phase: cap_reached — document created, show manual expiry date + value fields
  if (phase === "cap_reached" && savedDocumentId) {
    return (
      <CapReachedManualFields
        documentId={savedDocumentId}
        documentName={savedDocumentName}
        resetsAt={capResetsAt}
        onSuccess={handleConfirmSuccess}
      />
    );
  }

  // Phase: form
  return (
    <DocumentForm
      initialValues={initialValues}
      profileTypeId={profileTypeId}
      handleSubmit={handleSubmit}
      title="Add New Document"
      lockCategory={lockCategory}
      lockSubcategory={lockSubcategory}
      lockedSubcategoryId={lockedSubcategoryId}
    />
  );
};

// ---------------------------------------------------------------------------
// Inline sub-component: manual fields when verification cap is reached
// ---------------------------------------------------------------------------

interface CapReachedManualFieldsProps {
  documentId: number;
  documentName: string;
  resetsAt: string | null;
  onSuccess: () => void;
}

const CapReachedManualFields: React.FC<CapReachedManualFieldsProps> = ({
  documentId,
  documentName,
  resetsAt,
  onSuccess,
}) => {
  const companyId = useCurrentCompanyId() as number;
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [value, setValue] = useState("");

  const updateMutation = useMutation({
    mutationFn: (extraInfo: Record<string, unknown>) =>
      apiV1.patch(`companies/${companyId}/documents/${documentId}`, {
        extra_info: extraInfo,
      }),
    onSuccess: () => {
      showToast("Document saved successfully.", "Success", true);
      onSuccess();
    },
    onError: () => {
      showToast("Failed to save. Please try again.", "Error");
    },
  });

  const handleSave = () => {
    const extraInfo: Record<string, unknown> = {};
    if (expiryDate) {
      const yyyy = expiryDate.getFullYear();
      const mm = String(expiryDate.getMonth() + 1).padStart(2, "0");
      const dd = String(expiryDate.getDate()).padStart(2, "0");
      extraInfo.expiry_date = `${yyyy}-${mm}-${dd}`;
    }
    if (value) {
      extraInfo.value = value;
    }
    updateMutation.mutate(extraInfo);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <FiCheckCircle size={18} className="text-green-600" />
        <p className="text-sm font-medium text-gray-700">{documentName} saved</p>
      </div>

      <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3">
        <FiAlertTriangle size={18} className="shrink-0 text-amber-600" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Monthly verification cap reached</p>
          <p className="mt-0.5 text-xs">
            Automatic verification is unavailable.
            {resetsAt && (
              <> Resets {new Date(resetsAt).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}.</>
            )}
            {" "}You can enter the expiry date and value manually below.
          </p>
        </div>
      </div>

      <DateInputWithLabel
        label="Expiry Date"
        value={expiryDate}
        onChange={(date) => setExpiryDate(date)}
        testId="cap-field-expiry-date"
      />

      <TextInputWithLabel
        label="Value"
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. 20,000,000"
        testId="cap-field-value"
      />

      <button
        type="button"
        onClick={handleSave}
        disabled={updateMutation.isPending}
        className="w-full bg-blue-600 text-white rounded-md py-2 px-4 text-sm font-medium disabled:opacity-50"
        data-testid="cap-save-btn"
      >
        {updateMutation.isPending ? "Saving…" : "Save"}
      </button>
    </div>
  );
};
